import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { 
  Trophy, 
  Ticket, 
  DollarSign, 
  TrendingUp,
  ChevronLeft,
  Loader2,
  Calendar,
  Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const WinningsPage = () => {
  const [winningsData, setWinningsData] = useState(null);
  const [winningTickets, setWinningTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [winningsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/community/winnings`),
        axios.get(`${API}/community/winning-tickets?limit=50`)
      ]);
      setWinningsData(winningsRes.data);
      setWinningTickets(ticketsRes.data.tickets);
    } catch (error) {
      console.error("Failed to fetch winnings data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDrawDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('T')[0].split('-');
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${days[d.getDay()]}, ${dd}.${mm}.${d.getFullYear()}`;
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatLotteryName = (type) => {
    const names = {
      tatts_lotto: "Tatts Lotto",
      oz_lotto: "OZ Lotto",
      powerball: "Powerball",
      weekday_windfall: "Weekday Windfall"
    };
    return names[type] || type;
  };

  // Chart data for winnings by lottery
  const barChartData = winningsData ? {
    labels: Object.keys(winningsData.winnings_by_lottery).map(formatLotteryName),
    datasets: [
      {
        label: "Total Winnings ($)",
        data: Object.values(winningsData.winnings_by_lottery).map(l => l.total_winnings),
        backgroundColor: [
          "rgba(16, 185, 129, 0.8)",  // Emerald
          "rgba(59, 130, 246, 0.8)",  // Blue
          "rgba(255, 215, 0, 0.8)",   // Gold
          "rgba(168, 85, 247, 0.8)",  // Purple
        ],
        borderColor: [
          "rgba(16, 185, 129, 1)",
          "rgba(59, 130, 246, 1)",
          "rgba(255, 215, 0, 1)",
          "rgba(168, 85, 247, 1)",
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  } : null;

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Winnings by Lottery Type",
        color: "#fff",
        font: {
          size: 16,
          family: "Outfit, sans-serif",
          weight: "600"
        }
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleFont: { family: "Outfit, sans-serif" },
        bodyFont: { family: "DM Sans, sans-serif" },
        callbacks: {
          label: (context) => ` ${formatCurrency(context.raw)}`
        }
      }
    },
    scales: {
      x: {
        ticks: { color: "#a1a1aa" },
        grid: { color: "rgba(255, 255, 255, 0.05)" }
      },
      y: {
        ticks: { 
          color: "#a1a1aa",
          callback: (value) => formatCurrency(value)
        },
        grid: { color: "rgba(255, 255, 255, 0.05)" }
      }
    }
  };

  // Doughnut chart for ticket distribution
  const doughnutChartData = winningsData ? {
    labels: Object.keys(winningsData.tickets_by_lottery).map(formatLotteryName),
    datasets: [
      {
        data: Object.values(winningsData.tickets_by_lottery),
        backgroundColor: [
          "rgba(16, 185, 129, 0.8)",
          "rgba(59, 130, 246, 0.8)",
          "rgba(255, 215, 0, 0.8)",
          "rgba(168, 85, 247, 0.8)",
        ],
        borderColor: "#0a0a0a",
        borderWidth: 2,
      },
    ],
  } : null;

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: "#fff",
          font: { family: "DM Sans, sans-serif" },
          padding: 16
        }
      },
      title: {
        display: true,
        text: "Games by Lottery",
        color: "#fff",
        font: {
          size: 16,
          family: "Outfit, sans-serif",
          weight: "600"
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-aura-emerald mx-auto mb-4" />
          <p className="text-aura-text-secondary">Loading community winnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aura-bg relative">
      {/* Noise overlay */}
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />

      {/* Header */}
      <header className="bg-aura-bg/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="logo" data-testid="logo-link">
            <svg width="34" height="34" viewBox="0 0 100 100" className="glow">
              <defs>
                <radialGradient id="ball-winnings" cx="30%" cy="30%">
                  <stop offset="0%" stopColor="#444"/>
                  <stop offset="100%" stopColor="#000"/>
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#ball-winnings)" />
              <circle cx="50" cy="50" r="18" fill="white"/>
              <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
            </svg>
            <span className="logo-text">AURA</span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-white/10 text-aura-text-secondary hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-gold/10 border border-aura-gold/20 text-aura-gold text-sm font-medium mb-6">
            <Trophy className="w-4 h-4" />
            <span>Community Achievements</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
            Community <span className="gradient-text">Winnings</span>
          </h1>
          <p className="text-xl text-aura-text-secondary max-w-2xl mx-auto">
            {winningsData?.has_verified_wins
              ? "Real verified winnings from AURA-tracked community tickets"
              : "AURA is verifying tracked tickets against official draw results"}
          </p>
        </motion.div>

        {/* Total Winnings Counter */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-12"
        >
          <div className="inline-block p-8 rounded-2xl glass-emerald">
            <p className="text-aura-text-secondary text-lg mb-2">Total Community Winnings</p>
            {winningsData?.has_verified_wins ? (
              <p className="font-heading text-5xl md:text-7xl font-bold text-aura-gold" style={{ textShadow: '0 0 30px rgba(255, 215, 0, 0.5)' }} data-testid="total-winnings">
                {formatCurrency(winningsData.total_winnings)}
              </p>
            ) : (
              <div data-testid="total-winnings">
                <p className="font-heading text-3xl md:text-5xl font-bold text-aura-gold/70" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.3)' }}>
                  Verifying Results...
                </p>
                <p className="text-aura-text-muted text-sm mt-3">
                  AURA is cross-checking {winningsData?.total_valid_tickets || 0} tracked tickets against official lottery results
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-aura p-6 text-center"
          >
            <Ticket className="w-8 h-8 text-aura-emerald mx-auto mb-3" />
            <p className="font-heading text-3xl font-bold text-white">{winningsData?.total_valid_tickets || 0}</p>
            <p className="text-sm text-aura-text-muted">Total Valid Games</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-aura p-6 text-center"
          >
            <Trophy className="w-8 h-8 text-aura-gold mx-auto mb-3" />
            {winningsData?.has_verified_wins ? (
              <p className="font-heading text-3xl font-bold text-aura-gold">{winningsData.total_winning_tickets}</p>
            ) : (
              <p className="font-heading text-2xl font-bold text-aura-gold/60">Pending</p>
            )}
            <p className="text-sm text-aura-text-muted">Winning Games</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card-aura p-6 text-center"
          >
            <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-3" />
            {winningsData?.has_verified_wins ? (
              <p className="font-heading text-2xl font-bold text-aura-gold">
                {formatCurrency(winningsData.total_winnings)}
              </p>
            ) : (
              <p className="font-heading text-2xl font-bold text-aura-gold/60">Pending</p>
            )}
            <p className="text-sm text-aura-text-muted">Total Winnings</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="card-aura p-6 text-center"
          >
            <TrendingUp className="w-8 h-8 text-purple-400 mx-auto mb-3" />
            {winningsData?.has_verified_wins ? (
              <p className="font-heading text-2xl font-bold text-aura-gold">
                {formatCurrency(winningsData.average_win_per_winning_ticket)}
              </p>
            ) : (
              <p className="font-heading text-2xl font-bold text-aura-gold/60">Pending</p>
            )}
            <p className="text-sm text-aura-text-muted">Average Win / Ticket</p>
          </motion.div>
        </div>

        {/* Charts Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Bar Chart - Winnings by Lottery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="card-aura p-6"
          >
            {winningsData?.has_verified_wins ? (
              <div className="h-80">
                <Bar data={barChartData} options={barChartOptions} />
              </div>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center">
                <Trophy className="w-16 h-16 text-aura-gold/30 mb-4" />
                <p className="font-heading text-xl text-aura-gold/70">Winnings by Lottery</p>
                <p className="text-aura-text-muted text-sm mt-2">Charts will appear once wins are verified</p>
              </div>
            )}
          </motion.div>

          {/* Doughnut Chart - Ticket Distribution */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="card-aura p-6"
          >
            {winningsData && (winningsData.total_valid_tickets || 0) > 0 ? (
              <div className="h-80">
                <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
              </div>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center">
                <Ticket className="w-16 h-16 text-aura-emerald/30 mb-4" />
                <p className="font-heading text-xl text-aura-emerald/70">Games by Lottery</p>
                <p className="text-aura-text-muted text-sm mt-2">Distribution will show as games are tracked</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Winning Games Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="card-aura overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-aura-emerald" />
              Verified Winning Games
            </h2>
            <p className="text-sm text-aura-text-muted mt-1">
              Anonymous listing of verified community wins
            </p>
          </div>

          {winningTickets.length > 0 ? (
            <div className="divide-y divide-white/5">
              {winningTickets.map((ticket, i) => {
                const matchedNums = ticket.matched_numbers || [];
                const matchedSupps = ticket.matched_supps || [];
                return (
                  <div key={i} className="p-5 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-aura-gold/20 flex items-center justify-center">
                          <Trophy className="w-5 h-5 text-aura-gold" />
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {ticket.lottery_name || ticket.lottery_type?.replace('_', ' ')}
                          </p>
                          <p className="text-sm text-aura-text-muted">
                            {formatDrawDate(ticket.draw_date)} — Division {ticket.division || 'N/A'} — {ticket.matches || 0} numbers matched
                          </p>
                        </div>
                      </div>
                      <p className="font-heading text-lg font-bold text-aura-gold">
                        {formatCurrency(ticket.winnings || 0)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-13">
                      {(ticket.numbers || []).map((num, j) => {
                        const isMatched = matchedNums.includes(num);
                        const isSuppMatch = matchedSupps.includes(num);
                        return (
                          <span key={j} className={`lottery-ball small ${isMatched ? 'winner' : isSuppMatch ? 'supp-match' : ''}`} data-testid={`winning-ticket-ball-${i}-${j}`}>
                            {num}
                          </span>
                        );
                      })}
                      {ticket.powerball && (
                        <span className={`lottery-ball small powerball ${ticket.powerball_match ? 'winner' : ''}`}>
                          {ticket.powerball}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Trophy className="w-16 h-16 text-aura-gold/20 mx-auto mb-4" />
              <p className="font-heading text-xl text-aura-text-secondary mb-2">Verifying Community Results...</p>
              <p className="text-aura-text-muted text-sm">
                AURA is cross-checking tracked tickets against official draw results.
              </p>
              <p className="text-aura-text-muted text-sm mt-1">
                Verified winners will appear here automatically.
              </p>
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-center mt-12"
        >
          <p className="text-aura-text-secondary mb-4">
            Want to track your games and contribute to the community?
          </p>
          <Link to="/register">
            <Button className="btn-gold">
              Join AI Manifest Free
            </Button>
          </Link>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-aura-text-muted">
            This platform is an AI analysis tool. Not affiliated with The Lott. Always gamble responsibly.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WinningsPage;
